import { useRef, useState } from "react";

const THRESHOLD = 50;

/**
 * Horizontal swipe with a finger: the content follows the finger, and a swipe longer than THRESHOLD
 * calls onSwipeLeft (finger moving left, to see what comes next) or onSwipeRight.
 * Vertical moves are left to the browser, for scrolling (the element should have "touch-action: pan-y").
 */
const useSwipe = ({onSwipeLeft, onSwipeRight}) => {
  const [offset, setOffset] = useState(0);
  const gesture = useRef(null);
  const swiped = useRef(false);

  const onTouchStart = event => {
    const touch = event.touches[0];

    swiped.current = false;
    gesture.current = {x: touch.clientX, y: touch.clientY, horizontal: null, offset: 0};
  };

  const onTouchMove = event => {
    if (!gesture.current) {
      return;
    }

    const touch = event.touches[0];
    const dx = touch.clientX - gesture.current.x;
    const dy = touch.clientY - gesture.current.y;

    // The first clear move decides whether this is a swipe or a scroll
    if (gesture.current.horizontal === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
      gesture.current.horizontal = Math.abs(dx) > Math.abs(dy);
    }

    if (gesture.current.horizontal) {
      gesture.current.offset = dx;
      setOffset(dx);
    }
  };

  const onTouchEnd = () => {
    const current = gesture.current;

    gesture.current = null;
    setOffset(0);

    // Only the click that follows the swipe right away is cancelled
    if (current?.horizontal) {
      swiped.current = true;
      setTimeout(() => {
        swiped.current = false;
      }, 400);
    }

    if (current?.horizontal && current.offset < -THRESHOLD) {
      onSwipeLeft();
    } else if (current?.horizontal && current.offset > THRESHOLD) {
      onSwipeRight();
    }
  };

  // A swipe ending on a link or a button must not open it
  const onClickCapture = event => {
    if (swiped.current) {
      swiped.current = false;
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return {
    offset,
    swiping: offset !== 0,
    handlers: {onTouchStart, onTouchMove, onTouchEnd, onTouchCancel: onTouchEnd, onClickCapture}
  };
};

export default useSwipe;
